/**
 * Design tokens — the measurable half of "premium".
 *
 * Every number a template uses comes from here. Nothing is eyeballed inline,
 * because eyeballed numbers are exactly what makes a set of templates look
 * like a set of unrelated files instead of one library.
 *
 * All values are in CANVAS PIXELS at 300dpi (the print resolution the USA
 * template list targets: US Letter = 2550×3300px). `pt()` converts typographic
 * points to those pixels so type sizes stay in the vocabulary a print designer
 * actually thinks in.
 */

/** Print resolution every document in this library is authored at. */
export const DPI = 300;

/** Points → canvas px. 1pt = 1/72in, so at 300dpi one point is 4.1667px. */
export const pt = (points) => Math.round((points * DPI) / 72);

/** Inches → canvas px. */
export const inch = (inches) => Math.round(inches * DPI);

/** Canvas sizes. USA print standard is US Letter, not A4. */
export const CANVAS = {
  usLetterPortrait: { width: inch(8.5), height: inch(11) },   // 2550 × 3300
  usLetterLandscape: { width: inch(11), height: inch(8.5) },
};

/**
 * Spacing scale — a 4pt base compounded, not arbitrary. Templates only ever
 * use these steps, which is what produces the even optical rhythm that reads
 * as "designed" rather than "assembled".
 */
export const SPACE = {
  xxs: pt(4),    //  17px
  xs: pt(8),     //  33px
  sm: pt(12),    //  50px
  md: pt(18),    //  75px
  lg: pt(28),    // 117px
  xl: pt(42),    // 175px
  xxl: pt(64),   // 267px
  xxxl: pt(96),  // 400px
};

/**
 * Type scale — a 1.333 (perfect fourth) modular scale anchored on 11pt body.
 * Named by ROLE, not by size, so a template says `TYPE.h1` and the library
 * stays re-tunable from one place.
 */
export const TYPE = {
  micro: pt(8),      //  33px — legal lines, MLS numbers
  caption: pt(9.5),  //  40px — captions, footnotes
  eyebrow: pt(11),   //  46px — tracked-out label above a headline
  body: pt(12),      //  50px — paragraph copy
  bodyLg: pt(15),    //  63px — lead paragraph
  h3: pt(20),        //  83px
  h2: pt(30),        // 125px
  h1: pt(52),        // 217px
  display: pt(78),   // 325px — the one element that carries the page
  hero: pt(104),     // 433px — full-bleed statement numerals
};

/**
 * Line heights are unitless multipliers (the editor passes them straight to
 * CSS `line-height`). Display type gets tighter leading than body — the single
 * change that most separates professional typesetting from default typesetting.
 */
export const LEADING = {
  display: 0.94,
  heading: 1.06,
  subhead: 1.25,
  body: 1.5,
  caption: 1.4,
};

/**
 * Letter-spacing in canvas px. Large type gets NEGATIVE tracking, small caps
 * get generous POSITIVE tracking. Both are what the eye reads as expensive.
 */
export const TRACKING = {
  hero: -pt(3),
  display: -pt(2),
  h1: -pt(1.4),
  h2: -pt(0.8),
  normal: 0,
  wide: pt(1.6),
  eyebrow: pt(3.2),   // tracked-out uppercase label
  monogram: pt(6),
};

/** Corner radii. The library is deliberately low-radius — sharp reads premium. */
export const RADIUS = {
  none: 0,
  sm: pt(3),
  md: pt(8),
  lg: pt(16),
  pill: pt(999),
};

/** Stroke weights for rules and hairlines. */
export const RULE = {
  hair: Math.round(pt(0.6)),
  thin: Math.round(pt(1)),
  medium: Math.round(pt(2.5)),
  thick: Math.round(pt(6)),
};

/** Soft elevation. Used sparingly — one shadow per page at most. */
export const SHADOW = {
  card: { offsetX: 0, offsetY: pt(6), blur: pt(18), color: 'rgba(15,23,32,0.18)' },
  lift: { offsetX: 0, offsetY: pt(3), blur: pt(9), color: 'rgba(15,23,32,0.22)' },
};

/**
 * Print safety. Anything closer to the trim edge than `safe` risks being cut
 * by a commercial printer; the quality gate enforces it for every text element.
 */
export const PRINT = {
  bleed: inch(0.125),
  safe: inch(0.25),
};
